<template>
  <div v-show="true">
    <div v-if="isLoading" class="flex justify-center items-center space-x-2 mt-8 border border-gray-200 py-4 px-5 w-full text-lg rounded-full">
      <div class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status"></div>
      <div class="text-lg text-gray-500">Loading activities, please wait...</div>
    </div>

    <div v-if="recentActivities.length > 0">
      <div class="flex flex-col space-y-4 mt-6 mb-6">
        <div
          v-for="activity in recentActivities"
          :class="{
            'bg-pink-50': activity.action !== 'campaign_created' && activity.action !== 'honoree_donation_created',
            'bg-blue-50': activity.action === 'campaign_created',
            'bg-purple-100': activity.action === 'honoree_donation_created',
            'md:items-center': activity.action !== 'honoree_donation_created',
            'md:items-start': activity.action === 'honoree_donation_created'
          }"
          class="w-full text-lg flex flex-col md:flex-row activity-class rounded-3xl pr-6 pl-20 md:pr-8 md:pl-16 py-4 relative"
        >
          <div class="absolute -left-5 md:-left-8 -top-1 md:-top-[5px]" :class="{ 'md:-top-0.5': activity.action === 'honoree_donation_created' }">
            <div v-show="activity.action == `campaign_created`"  class="bg-blue text-white rounded-full h-20 w-20 mr-6 flex items-center justify-center text-2xl border-solid border-4 border-white">
              <img class="block w-3/5" src="/img/campaign-icon.png">
            </div>
            <!-- Additional icons based on activity.action can be added here -->
          </div>

          <div class="flex-grow md:pr-8">
            <!-- Campaign Created Activity -->
            <p v-if="activity.action == 'campaign_created'" class="text-base leading-tight">
              <p>
                <strong>{{ activity.subject }}</strong> created a campaign in support of the
                <span class="md:block" v-if="activity.sub_campaign_id != campaignId">
                  <a class="text-blue hover:text-blue-600" :href="campaignUrl(activity.sub_campaign_id)">
                    <strong>{{ activity.name }} campaign</strong>
                  </a>
                </span>
                <span class="md:block" v-else>
                  <strong class="text-blue">{{ activity.name }} campaign</strong>
                </span>
              </p>
            </p>
            <!-- Additional activity types can be handled here -->
          </div>          

          <!-- This is where we add the goal display for campaign created activities -->
          <div v-if="activity.action == 'campaign_created' && activity.sub_campaign_id != this.campaignId" class="flex flex-col items-start text-base leading-tight w-20 mt-3 md:mt-0">
            <span>Goal</span>
            <span class="text-blue font-bold">{{ formatCurrency(activity.campaignGoal) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More Button -->
    <a v-if="next_page_url" href="#!" @click="loadMore($event)" id="loadMore"
      :disabled="isLoadMoreDisabled"
      class="mb-3 bg-white border-2 border-blue hover:border-blue-700 hover:text-blue-700 text-blue block py-3 font-medium uppercase text-center text-lg mt-10 focus:outline-none focus:ring-blue-200 rounded-full transform transition-all duration-500 w-3/4 lg:w-1/2 mx-auto">
      <div class="flex items-center justify-center relative">
        <span class="text-center mx-auto">Load More</span>
        <div class="spinner-container">
          <div v-if="isLoadingMore" class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status"></div>
        </div>
      </div>
    </a>

    <div v-else-if="!isLoading && recentActivities.length <= 0" class="mb-6">
      <div class="flex flex-col mt-6 space-y-5">
        <div class="border border-gray-200 py-4 px-5 w-full text-lg flex items-center">
          <p>Be the first to <a href="#" class="text-blue hover:text-blue-600 rounded-full">donate</a> and support our cause.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'CampaignOnlyActivities',

    props: {
      campaignId: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        isLoading: true,
        isLoadingMore: false,
        recentActivities: [],
        next_page_url: '/api/open/campaigns/activity/' + this.campaignId,
        isLoadMoreDisabled: false,
      }
    },

    created() {
      this.isLoading = true
      this.isLoadingMore = false
      this.loadActivities()

      this.emitter.on('campaign-donation-update', (campaign) => {
        this.campaign = campaign
        this.next_page_url = '/api/open/campaigns/activity/' + this.campaignId
        this.recentActivities = []
        this.loadActivities()
      })
    },

    methods: {
      formatCurrency(amount) {
        if (!amount && amount !== 0) return '';

        let number = parseFloat(amount);
        if (isNaN(number)) {
          return amount;
        }

        return `$${number.toFixed(2)}`;
      },

      scroll(recentActivities) {
        window.onscroll = () => {
          let bottomOfWindow = document.documentElement.scrollTop + window.innerHeight === document.documentElement.offsetHeight

          if (bottomOfWindow) {
            this.isLoadingMore = true
            this.loadActivities()
          }
        }
      },

      loadMore(event) {
        event.preventDefault();
        this.isLoadMoreDisabled = true; // Disable the button
        this.isLoadingMore = true;      // Show the loading spinner
        this.loadActivities().then(() => {
          this.isLoadMoreDisabled = false; // Re-enable the button
          this.isLoadingMore = false;      // Hide the loading spinner
        });
      },

      async loadActivities() {
        if (this.next_page_url) {
          try {
            const response = await axios.get(this.next_page_url, { params: { activity: 'campaign' } });

            // Filter out the activity for the creation of the current campaign
            const filteredActivities = response.data.data.filter(activity => 
              !(activity.action === 'campaign_created' && activity.sub_campaign_id === this.campaignId)
            );

            this.recentActivities.push(...filteredActivities);
            this.next_page_url = response.data.next_page_url;
          } catch (error) {
            console.error('Error loading activities:', error);
          } finally {
            this.isLoading = false;
            this.isLoadingMore = false;
          }
        } else {
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      },

      campaignUrl(id) {
        return `/campaigns/${id}`
      },

      fundraiseUrl() {
        return '/create-my-campaign/'+this.campaignId
      }
    },
  }
</script>
<style scoped>
.st0{
  fill:#FFFFFF;
}
.spinner-border {
  border-color: transparent;
  border-top-color: #3490dc; /* Spinner color */
  border-right-color: #3490dc; /* Spinner color */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.spinner-space {
  width: 2rem; /* Same as spinner width */
  height: 2rem; /* Same as spinner height */
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-container {
  position: absolute;
  right: 1rem;
  width: 2rem; /* Same as spinner width */
  height: 2rem; /* Same as spinner height */
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-border {
  border-color: transparent;
  border-top-color: #3490dc; /* Spinner color */
  border-right-color: #3490dc; /* Spinner color */
}
</style>
